import type { AngleLabelConfig, LineLabelConfig } from '@examdojo/core/widget';
import type { ThemeColorName } from '@examdojo/styles';
import type { GeometryDisplayConfig } from '../../geometrypad.model';

/**
 * Compute angle label config from the scope system.
 *
 * Scope controls **auto-label generation only**, never geometry visibility.
 * The angle arc is always visible from scope's perspective — actual hiding
 * is done by explicit `subpart.visible` overrides or shape defaults.
 *
 * Uses text templates instead of contentVariant for name+value combinations
 * so the customName (from element.names) is respected rather than the
 * JSXGraph element's internal name.
 *
 * Returns { visible: true, label } where:
 * - visible: always true (scope never hides geometry)
 * - label: AngleLabelConfig or false based on names/values scope
 */
export function computeAngleScope(
  angleConfig: { prop: string; extProp?: string },
  givenKeys: Set<string>,
  geometryConfig: GeometryDisplayConfig,
  color: ThemeColorName,
  customName?: string,
): { visible: boolean; label: AngleLabelConfig | false } {
  const namesScope = geometryConfig.label?.names;
  const valuesScope = geometryConfig.label?.values;
  const isGiven = givenKeys.has(angleConfig.prop) || (angleConfig.extProp ? givenKeys.has(angleConfig.extProp) : false);

  const showName = namesScope === 'all' || namesScope === 'tiered' || (namesScope === 'given' && isGiven);
  const showValue = valuesScope === 'all' || valuesScope === 'tiered' || (valuesScope === 'given' && isGiven);

  if (!showName && !showValue) {
    return { visible: true, label: false };
  }

  const variant =
    namesScope === 'all' || valuesScope === 'all' || isGiven ? ('secondary' as const) : ('tertiary' as const);
  const labelName = customName ?? angleConfig.prop;

  // Use text templates to ensure the custom display name is used,
  // not the JSXGraph element's internal name from ctx.name.
  if (showName && showValue) {
    return {
      visible: true,
      label: { visible: true, variant, color, text: `∠${labelName} = {deg}°`, placement: 'outside' },
    };
  }
  if (showName) {
    return {
      visible: true,
      label: { visible: true, variant, color, text: labelName, placement: 'outside' },
    };
  }
  // values only
  return {
    visible: true,
    label: { visible: true, variant, color, contentVariant: 'deg', placement: 'outside' },
  };
}

/**
 * Compute edge label config from the scope system.
 *
 * Always uses text templates for name-containing labels so the custom
 * display name (from element.names) is used instead of the JSXGraph
 * element's internal name.
 */
export function computeEdgeScope(
  propertyName: string,
  givenKeys: Set<string>,
  geometryConfig: GeometryDisplayConfig,
  color: ThemeColorName,
  customName?: string,
): LineLabelConfig | false {
  const namesScope = geometryConfig.label?.names;
  const valuesScope = geometryConfig.label?.values;
  const isGiven = givenKeys.has(propertyName);

  const showName = namesScope === 'all' || namesScope === 'tiered' || (namesScope === 'given' && isGiven);
  const showValue = valuesScope === 'all' || valuesScope === 'tiered' || (valuesScope === 'given' && isGiven);

  if (!showName && !showValue) {
    return false;
  }

  const variant =
    namesScope === 'all' || valuesScope === 'all' || isGiven ? ('secondary' as const) : ('tertiary' as const);
  const labelName = customName ?? propertyName;
  const units = geometryConfig.label?.lengthUnit;

  if (showName && showValue) {
    return units
      ? { visible: true, variant, color, text: `${labelName} = {length} ${units}` }
      : { visible: true, variant, color, text: `${labelName} = {length}` };
  }

  if (showName) {
    return { visible: true, variant, color, text: labelName };
  }

  // values only
  return units
    ? { visible: true, variant, color, text: `{length} ${units}` }
    : { visible: true, variant, color, contentVariant: 'length' };
}
